import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wand2, RefreshCw, Image, Archive, Loader2, RotateCcw, ChevronRight, X, Pencil, Upload, Trash2 } from 'lucide-react';
import api from '../utils/api';
import AnalysisRenderer from '../components/AnalysisRenderer';
import PageHeader from '../components/PageHeader';

const CATEGORIES = ['신발', '가방', '액세서리', '의류', '생활용품', '스포츠용품', '기타'];
const STEPS = ['입력', 'AI 분석', '이미지 생성', '아카이브'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-1 mb-6">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-1">
          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold transition-colors ${
            i < current ? 'bg-brand text-white' : i === current ? 'bg-brand/20 text-brand border border-brand' : 'bg-gray-800 text-gray-600'
          }`}>
            {i < current ? '✓' : i + 1}
          </div>
          <span className={`text-xs ${i === current ? 'text-white' : 'text-gray-600'}`}>{s}</span>
          {i < STEPS.length - 1 && <div className="w-5 h-px bg-gray-800 mx-0.5" />}
        </div>
      ))}
    </div>
  );
}

export default function IdeaWorkshopPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('idea');
  const [ideaId, setIdeaId] = useState(null);
  const [analysisText, setAnalysisText] = useState('');
  const [images, setImages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageHint, setImageHint] = useState('');
  const [referenceImage, setReferenceImage] = useState(null); // { url, uploading }
  const analysisRef = useRef(null);
  const refFileRef = useRef(null);

  const [form, setForm] = useState({ productType: '', category: '', target: '', imageDescription: '', researchQuery: '' });

  const stream = async (id) => {
    setIsStreaming(true);
    setAnalysisText('');
    const token = localStorage.getItem('mfg_token');
    const response = await fetch(`/api/ideas/${id}/analyze`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              setAnalysisText(prev => {
                const next = prev + data.text;
                setTimeout(() => analysisRef.current?.scrollTo({ top: 9999, behavior: 'smooth' }), 0);
                return next;
              });
            }
          } catch {}
        }
      }
    }
    setIsStreaming(false);
  };

  const uploadReferenceImage = async (file, id) => {
    setReferenceImage({ url: null, uploading: true });
    const formData = new FormData();
    formData.append('image', file);
    try {
      const r = await api.post(`/ideas/${id}/reference-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReferenceImage({ url: r.data.url, uploading: false });
    } catch (e) {
      alert('업로드 실패: ' + (e.response?.data?.message || e.message));
      setReferenceImage(null);
    }
  };

  const removeReferenceImage = async () => {
    if (!ideaId) { setReferenceImage(null); return; }
    await api.delete(`/ideas/${ideaId}/reference-image`);
    setReferenceImage(null);
  };

  const startAnalysis = async () => {
    try {
      let id = ideaId;
      if (!id) {
        const r = await api.post('/ideas', { type: mode, ...form });
        id = r.data.id;
        setIdeaId(id);
        // 참고 이미지가 로컬에 선택됐으면 새 ideaId로 업로드
        if (refFileRef.current?.files?.[0]) {
          await uploadReferenceImage(refFileRef.current.files[0], id);
        }
      } else {
        await api.patch(`/ideas/${id}/inputs`, form);
      }
      setStep(1);
      await stream(id);
    } catch (e) {
      alert('오류: ' + (e.response?.data?.message || e.message));
    }
  };

  // 입력으로 돌아가기 (form 유지, ideaId 유지)
  const backToInput = () => {
    setStep(0);
    setAnalysisText('');
    setImages([]);
  };

  const reanalyze = async () => { await stream(ideaId); };

  const [imageProgress, setImageProgress] = useState({ current: 0, total: 3, label: '' });

  const generateImages = async () => {
    setIsGeneratingImages(true);
    setImages([]);
    setImageProgress({ current: 0, total: 3, label: '준비 중...' });
    setStep(2);

    const token = localStorage.getItem('mfg_token');
    const response = await fetch(`/api/ideas/${ideaId}/generate-images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageHint: imageHint.trim() }),
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.step === 'prompt') setImageProgress(p => ({ ...p, label: data.message }));
            if (data.step === 'generating') setImageProgress({ current: data.current - 1, total: data.total, label: `${data.label} 생성 중... (${data.current}/${data.total})` });
            if (data.step === 'image') setImages(prev => [...prev, data.image]);
            if (data.step === 'done') setImageProgress(p => ({ ...p, current: p.total, label: '완료' }));
            if (data.step === 'error') alert(data.message);
          } catch {}
        }
      }
    } finally {
      setIsGeneratingImages(false);
    }
  };

  const regenerateImage = async (imageId) => {
    setImages(prev => prev.map(img => img.id === imageId ? { ...img, loading: true } : img));
    try {
      const r = await api.post(`/ideas/${ideaId}/images/${imageId}/regenerate`);
      setImages(prev => prev.map(img => img.id === imageId ? r.data : img));
    } catch { setImages(prev => prev.map(img => img.id === imageId ? { ...img, loading: false } : img)); }
  };

  const toggleSelect = async (imageId) => {
    const r = await api.put(`/ideas/${ideaId}/images/${imageId}/select`);
    setImages(prev => prev.map(img => img.id === imageId ? r.data : img));
  };

  const archiveIdea = async () => {
    setIsSaving(true);
    try {
      await api.post(`/ideas/${ideaId}/archive`);
      navigate('/idea-archive');
    } catch (e) {
      alert('저장 실패');
      setIsSaving(false);
    }
  };

  const reset = () => {
    setStep(0); setIdeaId(null); setAnalysisText('');
    setImages([]); setIsStreaming(false); setIsSaving(false);
    setReferenceImage(null);
    setForm({ productType: '', category: '', target: '', imageDescription: '', researchQuery: '' });
  };

  const isIdeaMode = mode === 'idea';
  const canStart = isIdeaMode ? !!form.productType : !!form.researchQuery;

  return (
    <div>
      {/* 헤더 */}
      <PageHeader icon={Wand2} title="AI 워크숍">
        {step >= 1 && !isStreaming && (
          <button onClick={backToInput} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-800">
            <Pencil size={13} /> 입력 수정
          </button>
        )}
        {step > 0 && (
          <button onClick={reset} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800">
            <X size={14} /> 처음부터
          </button>
        )}
        {step === 0 && (
          <div className="flex bg-gray-800 rounded-lg p-0.5">
            {[['idea', '아이디어'], ['research', '리서치']].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${mode === v ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </PageHeader>

      <StepIndicator current={step} />

      <div className="space-y-4">
        {/* 0단계: 입력 */}
        {step === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
            {ideaId && (
              <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 border border-brand/20 rounded-lg">
                <Pencil size={12} className="text-brand flex-shrink-0" />
                <span className="text-brand text-xs">이전 입력값을 수정 중입니다. 수정 후 AI 분석 시작을 누르면 재생성됩니다.</span>
              </div>
            )}
            {isIdeaMode ? (
              <>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">상품 종류 <span className="text-brand">*</span></label>
                  <input value={form.productType} onChange={e => setForm(f => ({ ...f, productType: e.target.value }))}
                    placeholder="예: 샤워기헤드 거치대, 케이블 정리 클립, 치약 짜개"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">카테고리</label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(c => (
                      <button key={c} onClick={() => setForm(f => ({ ...f, category: c }))}
                        className={`px-3 py-1 rounded-full text-xs transition-colors ${form.category === c ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">타겟 고객</label>
                  <input value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
                    placeholder="예: 30대 직장 여성, 아웃도어를 즐기는 20~40대"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">
                    상세 설명 <span className="text-gray-600 font-normal">— 이미지에 그대로 반영됩니다</span>
                  </label>
                  <textarea value={form.imageDescription} onChange={e => setForm(f => ({ ...f, imageDescription: e.target.value }))}
                    placeholder={`소재·형태·색상·구조·크기 등을 구체적으로 적어주세요.\n예: PLA 소재의 탈착식 모듈형 샤워헤드로, 파스텔 색상의 큐브 노즐이 3개 달려있고 기존 샤워기 호스에 나사형으로 연결됩니다. 뚜껑형 캡으로 노즐을 교체할 수 있으며 전체적으로 미니멀한 화이트 바디에 컬러 포인트가 있습니다.`}
                    rows={5}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600 resize-none" />
                </div>
                {/* 참고 이미지 업로드 */}
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block flex items-center gap-1">
                    <Upload size={11} /> 참고 이미지 <span className="text-gray-600 font-normal">— AI가 분석해 이미지에 반영합니다 (선택)</span>
                  </label>
                  {referenceImage?.url ? (
                    <div className="relative w-32 h-32">
                      <img src={referenceImage.url} alt="참고" className="w-full h-full object-cover rounded-lg border border-gray-700" />
                      <button onClick={removeReferenceImage}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-400">
                        <X size={10} />
                      </button>
                    </div>
                  ) : referenceImage?.uploading ? (
                    <div className="w-32 h-32 flex items-center justify-center border border-gray-700 rounded-lg">
                      <Loader2 size={18} className="animate-spin text-gray-500" />
                    </div>
                  ) : (
                    <button onClick={() => refFileRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-700 rounded-lg text-gray-500 hover:text-white hover:border-gray-500 text-xs transition-colors">
                      <Upload size={13} /> 이미지 선택
                    </button>
                  )}
                  <input ref={refFileRef} type="file" accept="image/*" className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (ideaId) {
                        uploadReferenceImage(file, ideaId);
                      } else {
                        // ideaId 없으면 로컬 미리보기만 (startAnalysis 시 업로드)
                        setReferenceImage({ url: URL.createObjectURL(file), uploading: false, local: true });
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="text-gray-400 text-xs mb-1.5 block">리서치 주제 <span className="text-brand">*</span></label>
                <textarea value={form.researchQuery} onChange={e => setForm(f => ({ ...f, researchQuery: e.target.value }))}
                  placeholder="예: 국내 기능성 신발 시장 동향과 MZ세대 소비 패턴&#10;예: 3D프린팅 소재 트렌드와 신발 적용 사례"
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600 resize-none" />
              </div>
            )}
            <button onClick={startAnalysis} disabled={!canStart || isStreaming}
              className="flex items-center gap-2 bg-brand text-white text-sm px-4 py-2 rounded-lg hover:bg-brand/80 disabled:opacity-40 transition-colors">
              <Wand2 size={14} /> AI 분석 시작
            </button>
          </div>
        )}

        {/* 1단계: AI 분석 결과 */}
        {step >= 1 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <span className="text-sm font-medium text-white flex items-center gap-2">
                <Wand2 size={14} className="text-brand" />
                {isIdeaMode ? 'AI 아이디어 분석' : 'AI 리서치 리포트'}
                {isStreaming && <Loader2 size={12} className="text-brand animate-spin" />}
              </span>
              {!isStreaming && step === 1 && (
                <button onClick={reanalyze} className="flex items-center gap-1 text-gray-500 hover:text-white text-xs transition-colors">
                  <RotateCcw size={11} /> 재생성
                </button>
              )}
            </div>
            <div ref={analysisRef} className="px-4 py-4 max-h-[480px] overflow-y-auto">
              <AnalysisRenderer text={analysisText} streaming={isStreaming} />
            </div>
            {!isStreaming && step === 1 && (
              <div className="px-4 pb-3 flex justify-end gap-2">
                {isIdeaMode ? (
                  <button onClick={generateImages}
                    className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80">
                    <Image size={13} /> 이미지 생성 <ChevronRight size={13} />
                  </button>
                ) : (
                  <button onClick={archiveIdea} disabled={isSaving}
                    className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80 disabled:opacity-40">
                    {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />} 아카이브 저장
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* 2단계: 이미지 */}
        {step >= 2 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <span className="text-sm font-medium text-white flex items-center gap-2">
                <Image size={14} className="text-brand" /> 컨셉 이미지
                {isGeneratingImages && <Loader2 size={12} className="text-brand animate-spin" />}
              </span>
              {!isGeneratingImages && (
                <button onClick={generateImages} className="flex items-center gap-1 text-gray-500 hover:text-white text-xs transition-colors">
                  <RefreshCw size={11} /> 전체 재생성
                </button>
              )}
            </div>
            {/* 진행바 */}
            {isGeneratingImages && (
              <div className="px-4 pt-4 pb-2 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 flex items-center gap-1.5">
                    <Loader2 size={10} className="animate-spin" />{imageProgress.label}
                  </span>
                  <span className="text-gray-600">{imageProgress.current}/{imageProgress.total}</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-1">
                  <div className="h-full bg-brand rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(5, (imageProgress.current / imageProgress.total) * 100)}%` }} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 p-4">
              {isGeneratingImages
                ? <>
                    {images.map(img => (
                      <img key={img.id} src={img.url} alt="컨셉"
                        className="w-full aspect-square object-cover rounded-lg border border-gray-700" />
                    ))}
                    {Array.from({ length: 3 - images.length }).map((_, i) => (
                      <div key={i} className="aspect-square bg-gray-800 rounded-lg animate-pulse flex items-center justify-center">
                        <Loader2 size={18} className="text-gray-600 animate-spin" />
                      </div>
                    ))}
                  </>
                : images.map(img => (
                  <div key={img.id} className="relative group">
                    {img.loading ? (
                      <div className="aspect-square bg-gray-800 rounded-lg animate-pulse flex items-center justify-center">
                        <Loader2 size={18} className="text-gray-600 animate-spin" />
                      </div>
                    ) : (
                      <>
                        <img src={img.url} alt="컨셉"
                          className={`w-full aspect-square object-cover rounded-lg border-2 transition-all ${img.isSelected ? 'border-brand' : 'border-gray-800'}`} />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity flex flex-col items-center justify-center gap-2">
                          <button onClick={() => toggleSelect(img.id)}
                            className={`px-3 py-1 rounded-full text-xs font-medium ${img.isSelected ? 'bg-brand text-white' : 'bg-white text-gray-900'}`}>
                            {img.isSelected ? '✓ 선택됨' : '선택'}
                          </button>
                          <button onClick={() => regenerateImage(img.id)}
                            className="flex items-center gap-1 px-2 py-1 bg-gray-800/80 text-gray-300 rounded-full text-xs">
                            <RotateCcw size={9} /> 재생성
                          </button>
                        </div>
                        {img.isSelected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-brand rounded-full flex items-center justify-center">
                            <span className="text-white text-[10px] font-bold">✓</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
            </div>
            {!isGeneratingImages && images.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-800 flex items-center justify-between">
                <span className="text-gray-600 text-xs">{images.filter(i => i.isSelected).length}장 선택</span>
                <button onClick={archiveIdea} disabled={isSaving}
                  className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80 disabled:opacity-40">
                  {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />} 아카이브 저장
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
